import { Injectable, OnDestroy, inject } from '@angular/core';

import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  Subject,
  Subscription,
  distinctUntilChanged,
  map,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';

import { MonitoringLogger, mapErrorForMonitoring } from '@fcom/core';
import { finShare, triggerWhenPreviousCompletes } from '@fcom/rx';
import { unsubscribe } from '@fcom/core/utils';

@Injectable()
export class QueueService implements OnDestroy {
  private monitoringLogger = inject(MonitoringLogger);

  private currentOffer$: Subject<string> = new BehaviorSubject('unknown_or_refreshed_page');
  private currentQueue$: Observable<Subject<Observable<unknown>>> = new ReplaySubject(1);
  private currentCancelPayment$ = new Subject<void>();
  public cancelServiceAvailability$ = new Subject<void>();

  private subscriptions = new Subscription();

  constructor() {
    this.currentQueue$ = this.currentOffer$.pipe(
      distinctUntilChanged(),
      map(() => new Subject<Observable<unknown>>()),
      finShare()
    );
    this.subscriptions.add(
      this.currentQueue$.pipe(switchMap((queue) => queue.pipe(triggerWhenPreviousCompletes()))).subscribe()
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  sendToQueue(actionName: string, obs$: Observable<unknown>, cancelStream$: Observable<unknown>): void {
    let canceledBeforeTrigger = false;
    const subscription = new Subscription();
    subscription.add(cancelStream$.subscribe(() => (canceledBeforeTrigger = true)));

    this.subscriptions.add(
      this.currentQueue$.pipe(take(1)).subscribe((queue) => {
        const task$ = new Observable((o) => {
          try {
            if (canceledBeforeTrigger) {
              o.complete();
            } else {
              subscription.add(
                // eslint-disable-next-line rxjs-x/no-nested-subscribe, rxjs-x/no-subscribe-in-pipe
                obs$.subscribe({
                  complete: () => o.complete(),
                  error: () => o.complete(),
                })
              );
            }
          } catch (error) {
            this.monitoringLogger.error('Queueing action failed', {
              actionName,
              error: mapErrorForMonitoring(error),
            });
            o.error(error);

            // eslint-disable-next-line rxjs-x/no-redundant-notify
            o.complete();
          }
          return () => {
            o.complete();
            unsubscribe(subscription);
          };
        }).pipe(takeUntil(cancelStream$));
        queue.next(task$);
      })
    );
  }

  public startQueue(queueId: string): void {
    this.currentOffer$.next(queueId);
  }

  public cancelOngoingPayment(): void {
    return this.currentCancelPayment$.next();
  }

  get cancelPayment(): Subject<void> {
    return this.currentCancelPayment$;
  }

  /**
   * Replays any event from the stream after this method is called.
   * Completes the stream after the first event.
   */
  toCancelEvent = <T>(obs$: Observable<T>): Observable<T> => obs$.pipe(finShare());
}
